import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  BuildingOfficeIcon,
  MapPinIcon,
  CircleStackIcon,
  SparklesIcon,
  CheckCircleIcon,
  ArrowRightIcon,
  ChartBarIcon,
  CloudArrowUpIcon,
  XMarkIcon,
  GlobeAltIcon,
} from '@heroicons/react/24/outline';
import { OnboardingDataUploadPanel } from '@saas-foundation/shared-frontend/onboarding';
import PayerListUploadPanel from './PayerListUploadPanel';
import {
  addOrganizationLocation,
  completeChecklistItem,
  completeOnboarding,
  fetchOnboardingManifest,
  fetchOnboardingStatus,
  fetchOrganizationCurrent,
  listOnboardingScenarios,
  seedDefaultScenario,
  seedScenario,
  updateOrganization,
} from '../../services/onboardingApi';
import { markStepRun, STEP_COUNT } from './onboardingSteps';

const COMMON_SPECIALTIES = [
  'Internal Medicine', 'Family Practice', 'Cardiology', 'Orthopedics', 'Pediatrics',
  'Dermatology', 'Neurology', 'Psychiatry', 'Oncology', 'Emergency Medicine',
  'Behavioral Health', 'Radiology', 'General Practice', 'Multi-Specialty',
];

const QUICK_START_LINKS = [
  { name: 'RCM Data Upload', path: '/practice-data?module=x12-pipeline&lookback=90&dest=both', description: 'Add more 837/835 history' },
  { name: 'ERA Analytics', path: '/eob?tab=volume', description: 'See payer volume and trends' },
  { name: 'Work Queue', path: '/work-queue', description: 'Kai + human task routing' },
  { name: 'Denials', path: '/denials', description: 'Appeals and recovery' },
  { name: 'Help & Guides', path: '/help', description: 'Getting started per service' },
];

export default function OnboardingWizard({
  mode = 'signup',
  status,
  setStatus,
  step,
  setStep,
  onStepFinished,
  onCompleteSuccess,
  showStepper = true,
  showHeader = false,
  compact = false,
}) {
  const navigate = useNavigate();
  const isSettings = mode === 'settings';

  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [orgName, setOrgName] = useState('');
  const [specialties, setSpecialties] = useState([]);
  const [specialtyInput, setSpecialtyInput] = useState('');
  const [locationName, setLocationName] = useState('');
  const [locationAddress, setLocationAddress] = useState('');
  const [seedLoading, setSeedLoading] = useState(false);
  const [scenarios, setScenarios] = useState([]);
  const [selectedScenarioId, setSelectedScenarioId] = useState('era_analytics_showcase');
  const [manifest, setManifest] = useState(null);
  const [completing, setCompleting] = useState(false);
  const [refreshingIntegrations, setRefreshingIntegrations] = useState(false);

  const organizationId = status?.organization_id;

  useEffect(() => {
    if (!organizationId) return;
    fetchOrganizationCurrent()
      .then((res) => {
        const org = res?.organization || res;
        if (org?.name) setOrgName(org.name);
        if (Array.isArray(org?.specialties)) setSpecialties(org.specialties);
      })
      .catch(() => {});
  }, [organizationId]);

  useEffect(() => {
    if (status?.organization_name && !orgName) setOrgName(status.organization_name);
  }, [status?.organization_name, orgName]);

  useEffect(() => {
    if (step !== 5) return;
    listOnboardingScenarios()
      .then((list) => {
        const items = Array.isArray(list) ? list : [];
        setScenarios(items);
        const defaultScenario = items.find((s) => s.forced_default) || items[0];
        if (defaultScenario?.id) setSelectedScenarioId(defaultScenario.id);
      })
      .catch(() => {});
  }, [step]);

  useEffect(() => {
    if (step !== 6 || !organizationId) return;
    fetchOnboardingManifest(organizationId)
      .then(setManifest)
      .catch(() => {});
  }, [step, organizationId]);

  const notifyStepFinished = (stepId) => {
    if (organizationId) markStepRun(organizationId, stepId);
    onStepFinished?.(stepId);
  };

  const refreshStatus = async () => {
    const data = await fetchOnboardingStatus();
    setStatus(data);
    return data;
  };

  const addSpecialty = (value) => {
    const v = (typeof value === 'string' ? value : specialtyInput).trim();
    if (v && !specialties.includes(v)) setSpecialties([...specialties, v]);
    setSpecialtyInput('');
  };

  const removeSpecialty = (value) => setSpecialties(specialties.filter((x) => x !== value));

  const handleUpdateOrg = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    if (!orgName.trim()) {
      setError('Organization name is required.');
      return;
    }
    if (!specialties.length) {
      setError('Add at least one specialty.');
      return;
    }
    try {
      await updateOrganization({ name: orgName.trim(), specialties });
      setSuccess('Organization updated.');
      setStatus((s) => (s ? {
        ...s,
        needs_organization_setup: false,
        needs_specialty: false,
        current_step: 'integrations',
      } : s));
      notifyStepFinished('organization');
      if (isSettings) {
        setStep(null);
      } else {
        setStep(3);
      }
    } catch (err) {
      setError(err.message || 'Failed to update organization.');
    }
  };

  const handleAddLocation = async (e) => {
    e.preventDefault();
    setError('');
    if (!locationName.trim()) return;
    try {
      await addOrganizationLocation({
        name: locationName.trim(),
        address: locationAddress.trim() || undefined,
        is_primary: true,
      });
      setSuccess('Location added.');
      setLocationName('');
      setLocationAddress('');
      notifyStepFinished('location');
      if (isSettings) setStep(null);
    } catch (err) {
      setError(err.message || 'Failed to add location.');
    }
  };

  const markDataUploadStep = async () => {
    if (!organizationId) return;
    try {
      await completeChecklistItem('upload_rcm_data', organizationId);
    } catch {
      /* checklist item may not exist until scenario seed */
    }
    notifyStepFinished('data_upload');
  };

  const handleDataContinue = async () => {
    await markDataUploadStep();
    if (isSettings) {
      setStep(null);
    } else {
      setStep(4);
    }
  };

  const handleIntegrationsRefresh = async () => {
    setRefreshingIntegrations(true);
    setError('');
    try {
      const data = await refreshStatus();
      if (!data.needs_integrations) {
        setSuccess('Integrations configured.');
        notifyStepFinished('integrations');
        if (isSettings) {
          setStep(null);
        } else {
          setStep(5);
        }
      } else {
        setError('Integrations are not complete yet. Open Integrations, save your EHR and clearinghouse choices, then check again.');
      }
    } catch (err) {
      setError(err.message || 'Could not refresh onboarding status.');
    } finally {
      setRefreshingIntegrations(false);
    }
  };

  const handleSeed = async () => {
    setError('');
    setSeedLoading(true);
    try {
      const scenarioId = selectedScenarioId || 'era_analytics_showcase';
      const result = scenarios.length > 0
        ? await seedScenario(scenarioId, organizationId)
        : await seedDefaultScenario(organizationId);
      setSuccess(result?.message || 'Your ERA analytics demo is ready.');
      setStatus((s) => (s ? {
        ...s,
        scenario_loaded: true,
        scenario_id: result?.scenario_id || scenarioId,
        current_step: 'complete',
      } : s));
      notifyStepFinished('scenario');
      if (isSettings) {
        setStep(null);
      } else {
        setStep(6);
      }
    } catch (err) {
      setError(err.message || 'Failed to load guided scenario. Please retry or contact support.');
    } finally {
      setSeedLoading(false);
    }
  };

  const handleComplete = async () => {
    setError('');
    setCompleting(true);
    try {
      await completeOnboarding();
      notifyStepFinished('complete');
      if (onCompleteSuccess) {
        onCompleteSuccess();
      } else if (!isSettings) {
        navigate('/dashboard/operational', { replace: true });
      } else {
        await refreshStatus();
        setSuccess('Onboarding marked complete.');
        setStep(null);
      }
    } catch (err) {
      setError(err.message || 'Failed to complete onboarding.');
    } finally {
      setCompleting(false);
    }
  };

  const canCompleteOnboarding = status?.user_role === 'owner';
  const wrapperClass = compact
    ? 'bg-white border border-gray-200 rounded-xl shadow-sm p-6'
    : 'bg-white border border-gray-200 rounded-xl shadow-sm p-6';

  return (
    <div>
      {showHeader && (
        <div className="text-center mb-6">
          <div className="inline-flex items-center justify-center w-12 h-12 rounded-full bg-blue-100 text-blue-600 mb-3">
            <SparklesIcon className="w-7 h-7" />
          </div>
          <h2 className="text-xl font-bold text-gray-900">Setup wizard</h2>
          <p className="mt-1 text-sm text-gray-600">Run onboarding steps manually — progress is saved for your organization.</p>
        </div>
      )}

      {showStepper && (
        <div className="flex items-center justify-center gap-2 mb-6 flex-wrap">
          {Array.from({ length: STEP_COUNT }, (_, i) => i + 1).map((s) => (
            <div
              key={s}
              className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-medium ${
                step >= s ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
              }`}
            >
              {step > s ? <CheckCircleIcon className="w-5 h-5" /> : s}
            </div>
          ))}
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex justify-between gap-2">
          <span>{error}</span>
          <button type="button" onClick={() => setError('')}><XMarkIcon className="w-5 h-5" /></button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">{success}</div>
      )}

      {step === 1 && (
        <div className={wrapperClass}>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-1">
            <BuildingOfficeIcon className="w-6 h-6 text-blue-600" />
            Organization & specialty
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Name your organization and choose at least one specialty so we can tailor your demo.
          </p>
          <form onSubmit={handleUpdateOrg} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Organization name *</label>
              <input
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={orgName}
                onChange={(e) => setOrgName(e.target.value)}
                placeholder="e.g. Lakeside Medical Group"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Specialty *</label>
              <div className="flex flex-wrap gap-2 mb-2">
                {COMMON_SPECIALTIES.slice(0, 8).map((s) => (
                  <button key={s} type="button" onClick={() => addSpecialty(s)} className="px-3 py-1 text-xs border border-gray-300 rounded-full hover:bg-gray-50">
                    + {s}
                  </button>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                  value={specialtyInput}
                  onChange={(e) => setSpecialtyInput(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSpecialty())}
                  placeholder="Or type and press Enter"
                />
                <button type="button" onClick={() => addSpecialty()} className="px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Add</button>
              </div>
              {specialties.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                  {specialties.map((s) => (
                    <span key={s} className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm">
                      {s}
                      <button type="button" onClick={() => removeSpecialty(s)}><XMarkIcon className="w-4 h-4" /></button>
                    </span>
                  ))}
                </div>
              )}
            </div>
            <button type="submit" className="w-full py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
              Save{isSettings ? '' : ' and continue'}
            </button>
          </form>
          {!isSettings && (
            <div className="mt-4 text-center">
              <button type="button" onClick={() => setStep(2)} className="text-sm text-gray-500 hover:text-gray-700">
                Next: Add location (optional)
              </button>
            </div>
          )}
          {isSettings && (
            <div className="mt-4 text-center">
              <button type="button" onClick={() => setStep(null)} className="text-sm text-gray-500 hover:text-gray-700">Close</button>
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div className={wrapperClass}>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-1">
            <MapPinIcon className="w-6 h-6 text-blue-600" />
            Primary location (optional)
          </h2>
          <p className="text-sm text-gray-600 mb-4">Add a practice or billing location. You can add more in Settings later.</p>
          <form onSubmit={handleAddLocation} className="space-y-4">
            <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" value={locationName} onChange={(e) => setLocationName(e.target.value)} placeholder="Location name" />
            <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" value={locationAddress} onChange={(e) => setLocationAddress(e.target.value)} placeholder="Address" />
            <div className="flex gap-2 flex-wrap">
              <button type="submit" disabled={!locationName.trim()} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50">Add location</button>
              {!isSettings && (
                <button type="button" onClick={() => setStep(3)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Skip to data upload</button>
              )}
              {isSettings && (
                <button type="button" onClick={() => { notifyStepFinished('location'); setStep(null); }} className="px-4 py-2 border border-gray-300 rounded-lg text-sm">Skip</button>
              )}
            </div>
          </form>
          <div className="mt-4 text-center flex justify-center gap-4">
            {!isSettings && <button type="button" onClick={() => setStep(1)} className="text-sm text-gray-500 hover:text-gray-700">Back</button>}
            {isSettings && <button type="button" onClick={() => setStep(null)} className="text-sm text-gray-500 hover:text-gray-700">Close</button>}
          </div>
        </div>
      )}

      {step === 3 && (
        <div className={wrapperClass}>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-1">
            <CloudArrowUpIcon className="w-6 h-6 text-blue-600" />
            Upload your 837 &amp; 835 data
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Optional but recommended — upload a ZIP from the last 30–90 days so Denials, ERA, and analytics reflect your real payers.
          </p>
          <OnboardingDataUploadPanel
            organizationId={organizationId}
            onMarkComplete={markDataUploadStep}
            onSkip={handleDataContinue}
            onContinue={handleDataContinue}
            embedHeight={480}
            showFullPageLink
          />
          <div className="mt-4">
            <PayerListUploadPanel />
          </div>
          <div className="mt-4 text-center flex justify-center gap-4">
            {!isSettings && <button type="button" onClick={() => setStep(2)} className="text-sm text-gray-500 hover:text-gray-700">Back</button>}
            {isSettings && <button type="button" onClick={() => setStep(null)} className="text-sm text-gray-500 hover:text-gray-700">Close</button>}
          </div>
        </div>
      )}

      {step === 4 && (
        <div className={wrapperClass}>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-1">
            <GlobeAltIcon className="w-6 h-6 text-blue-600" />
            EHR &amp; clearinghouse
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Choose your EHR and clearinghouse in Integrations. This is required before you can finish onboarding.
          </p>
          {status?.needs_integrations ? (
            <div className="space-y-3">
              <button
                type="button"
                onClick={() => navigate('/integrations')}
                className="w-full py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700"
              >
                Open Integrations
              </button>
              <button
                type="button"
                onClick={handleIntegrationsRefresh}
                disabled={refreshingIntegrations}
                className="w-full py-2.5 border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50"
              >
                {refreshingIntegrations ? 'Checking…' : "I've configured — check status"}
              </button>
            </div>
          ) : (
            <div className="space-y-3">
              <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3">
                Integrations configured{status?.selected_ehr ? ` (${status.selected_ehr})` : ''}.
              </p>
              {!isSettings && (
                <button type="button" onClick={() => setStep(5)} className="w-full py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">
                  Continue to guided scenario
                </button>
              )}
            </div>
          )}
          <div className="mt-4 text-center flex justify-center gap-4">
            {!isSettings && <button type="button" onClick={() => setStep(3)} className="text-sm text-gray-500 hover:text-gray-700">Back</button>}
            {isSettings && (
              <>
                <button type="button" onClick={() => navigate('/integrations')} className="text-sm text-blue-600 hover:text-blue-800">Open Integrations</button>
                <button type="button" onClick={() => setStep(null)} className="text-sm text-gray-500 hover:text-gray-700">Close</button>
              </>
            )}
          </div>
        </div>
      )}

      {step === 5 && (
        <div className={wrapperClass}>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-1">
            <CircleStackIcon className="w-6 h-6 text-blue-600" />
            Load your guided scenario
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            We load synthetic demo data so you can explore ERA analytics and Work Queue on day one. Re-running replaces demo data for this scenario.
          </p>
          <div className="space-y-4">
            {(scenarios.length > 0 ? scenarios : [{ id: 'era_analytics_showcase', name: 'ERA Analytics Showcase', tagline: 'See payer behavior from remittance data.', forced_default: true }]).map((scenario) => (
              <label
                key={scenario.id}
                className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer ${selectedScenarioId === scenario.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200'}`}
              >
                <input type="radio" name="scenario" checked={selectedScenarioId === scenario.id} onChange={() => setSelectedScenarioId(scenario.id)} className="mt-1" />
                <div>
                  <p className="font-medium text-gray-900">{scenario.name}</p>
                  <p className="text-sm text-gray-600">{scenario.tagline}</p>
                  {scenario.forced_default && (
                    <span className="inline-block mt-1 text-xs text-blue-700 bg-blue-100 px-2 py-0.5 rounded">Recommended</span>
                  )}
                </div>
              </label>
            ))}
            <button
              type="button"
              onClick={handleSeed}
              disabled={seedLoading}
              className="w-full py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {seedLoading ? 'Loading scenario…' : status?.scenario_loaded ? 'Re-load guided scenario' : 'Load guided scenario'}
            </button>
            <p className="text-xs text-center text-gray-500">Demo patients are fictional — synthetic PHI-like names only.</p>
          </div>
          <div className="mt-4 text-center flex justify-center gap-4">
            {!isSettings && <button type="button" onClick={() => setStep(4)} className="text-sm text-gray-500 hover:text-gray-700">Back</button>}
            {isSettings && <button type="button" onClick={() => setStep(null)} className="text-sm text-gray-500 hover:text-gray-700">Close</button>}
          </div>
        </div>
      )}

      {step === 6 && (
        <div className={wrapperClass}>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-1">
            <ChartBarIcon className="w-6 h-6 text-green-600" />
            {status?.onboarding_completed ? 'Onboarding complete' : 'Your ERA analytics are ready'}
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            Explore highlights from your guided demo, then open the dashboard. Your product tour checklist stays in the bottom-right corner.
          </p>

          {(manifest?.highlights || []).length > 0 && (
            <div className="grid gap-3 mb-4 sm:grid-cols-3">
              {manifest.highlights.slice(0, 3).map((h) => (
                <div key={h.label} className="rounded-lg border border-gray-200 p-3 bg-gray-50">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Highlight</p>
                  <p className="text-sm font-medium text-gray-900 mt-1">{h.label}</p>
                </div>
              ))}
            </div>
          )}

          <div className="rounded-lg bg-amber-50 border border-amber-200 p-3 mb-4 text-sm text-amber-900">
            Guided demo environment — synthetic data only. Remove anytime in Settings.
          </div>

          <ul className="space-y-2 mb-4">
            {QUICK_START_LINKS.map((link) => (
              <li key={link.name}>
                <button
                  type="button"
                  onClick={() => navigate(link.path)}
                  className="w-full flex items-center justify-between p-3 rounded-lg border border-gray-200 hover:bg-gray-50 text-left"
                >
                  <span>
                    <span className="font-medium text-gray-900 block">{link.name}</span>
                    <span className="text-xs text-gray-500">{link.description}</span>
                  </span>
                  <ArrowRightIcon className="w-5 h-5 text-gray-400 shrink-0" />
                </button>
              </li>
            ))}
          </ul>

          {status?.onboarding_completed ? (
            <p className="text-sm text-green-800 bg-green-50 border border-green-200 rounded-lg p-3">
              This organization has completed onboarding. You can re-run any step above if needed.
            </p>
          ) : canCompleteOnboarding ? (
            <button
              type="button"
              onClick={handleComplete}
              disabled={completing}
              className="w-full py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
            >
              {completing ? 'Finishing…' : 'Mark onboarding complete'}
            </button>
          ) : (
            <p className="text-sm text-amber-900 bg-amber-50 border border-amber-200 rounded-lg p-3">
              Only the organization owner can mark onboarding complete. Admins can run all other setup steps.
            </p>
          )}

          {isSettings && (
            <div className="mt-4 text-center">
              <button type="button" onClick={() => setStep(null)} className="text-sm text-gray-500 hover:text-gray-700">Close</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
